import Diode from 'diode';
import { canIncrementWithGuestPool } from './ticket-quantity-limits';

var NumberStepper = {
  el: '[data-component="number-stepper"]',

  ui: {
    decrementButton: '[data-number-stepper="decrement-button"]',
    incrementButton: '[data-number-stepper="increment-button"]',
    input: '[data-number-stepper="input"]',
  },

  events: {
    'click {decrementButton}': 'decrement',
    'click {incrementButton}': 'increment',
    'change {input}': 'handleChange',
  },

  // These custom events are used to trigger alt button text in:
  // order-summary.js
  customIncrement: new CustomEvent('numberStepperIncrement'),
  customDecrement: new CustomEvent('numberStepperDecrement'),

  initialize: function () {
    Diode(this);
    const value = this.getInputValue();
    const absoluteMax = parseInt(this.ui.input[0].max, 10) || null;
    const typeMax = this.el.dataset.typeMax
      ? parseInt(this.el.dataset.typeMax, 10)
      : absoluteMax;
    const guestMax = this.el.dataset.guestMax
      ? parseInt(this.el.dataset.guestMax, 10)
      : null;
    const sectionLimit = this.el.dataset.sectionLimit
      ? parseInt(this.el.dataset.sectionLimit, 10)
      : null;

    const canDecrement = value > 0;
    const canIncrement = absoluteMax == null || absoluteMax > value;

    this.state = {
      canDecrement: canDecrement,
      canIncrement: canIncrement,
      max: absoluteMax,
      typeMax: typeMax,
      guestMax: guestMax,
      sectionLimit: sectionLimit,
    };
    const isInsideTicketQuantityForm = Boolean(
      this.el.closest('[data-component="ticket-number-stepper"]'),
    );
    this.isTicketQuantityChild =
      !this.el.dataset.component && isInsideTicketQuantityForm;
    this.hasGuestPool = guestMax != null;
    this.hasSectionLimit = sectionLimit != null;
    this.isSharedTotal = this.el.hasAttribute('data-ticket-quantity-node');
    this.isFullyDisabled = Boolean(this.el.dataset.disabled);
    this.updateState();

    this.ui.decrementButton[0].classList.remove('hide');
    this.ui.incrementButton[0].classList.remove('hide');

    this.render();
  },

  change: function (addend = 0) {
    this.ui.input[0].value = this.getInputValue() + addend;

    // programmatically changing a number input does not dispatch its change event
    // we simulate the built-in event
    const event = new InputEvent('change', {
      bubbles: true,
    });
    this.ui.input[0].dispatchEvent(event);
  },

  decrement: function (_e) {
    if (!this.state.canDecrement) {
      return;
    }

    this.change(-1);
    window.dispatchEvent(this.customDecrement);
  },

  increment: function (_e) {
    if (!this.state.canIncrement) {
      return;
    }

    this.change(1);
    window.dispatchEvent(this.customIncrement);
  },

  render: function () {
    if (this.isFullyDisabled) {
      this.ui.input[0].setAttribute('readonly', 'readonly');
    }
    if (this.state.canDecrement) {
      this.ui.decrementButton[0].removeAttribute('disabled');
      this.ui.decrementButton[0].classList.remove('color-disabled');
      this.ui.decrementButton[0].classList.add('color-link');
    } else {
      this.ui.decrementButton[0].setAttribute('disabled', '');
      this.ui.decrementButton[0].classList.add('color-disabled');
      this.ui.decrementButton[0].classList.remove('color-link');
    }

    if (this.state.canIncrement) {
      this.ui.incrementButton[0].removeAttribute('disabled');
      this.ui.incrementButton[0].classList.remove('color-disabled');
      this.ui.incrementButton[0].classList.add('color-link');
    } else {
      this.ui.incrementButton[0].setAttribute('disabled', '');
      this.ui.incrementButton[0].classList.add('color-disabled');
      this.ui.incrementButton[0].classList.remove('color-link');
    }
  },

  handleChange: function () {
    this.clampInputValue();

    if (this.isTicketQuantityChild) {
      this.publish();
      return;
    }
    this.updateState();
  },

  updateState: function () {
    if (this.isFullyDisabled) {
      this.state.canDecrement = false;
      this.state.canIncrement = false;
      this.render();
      return;
    }
    const value = this.getInputValue();
    this.state.canDecrement = value > 0;

    if (this.state.max) {
      this.state.canIncrement = value < this.state.max;
    }
    this.render();
  },

  // this method is called from app/frontend/src/components/ticket-number-stepper.js
  // via `Diode.publish`
  _updateStateFromTotalTicketQuantity: function (totalTicketQuantity) {
    const value = this.getInputValue();
    this.state.canIncrement =
      totalTicketQuantity < this.state.max && value < this.state.max;
    this.state.canDecrement = value > 0;
    this.render();
  },

  _updateStateFromGuestPool: function ({
    groupTotal,
    guestRemaining,
    sectionTotal,
    sectionLimit,
  }) {
    const value = this.getInputValue();

    this.state.canIncrement = canIncrementWithGuestPool({
      groupTotal: groupTotal,
      typeMax: this.state.typeMax,
      guestRemaining: guestRemaining,
      value: value,
      absoluteMax: this.state.max,
      sectionTotal: sectionTotal,
      sectionLimit: sectionLimit,
    });
    this.state.canDecrement = value > 0;
    this.render();
  },

  _updateStateFromSectionLimit: function ({ sectionTotal, sectionLimit }) {
    const value = this.getInputValue();
    const absoluteMax = this.state.max;

    this.state.canIncrement =
      sectionTotal < sectionLimit &&
      (absoluteMax == null || value < absoluteMax);
    this.state.canDecrement = value > 0;
    this.render();
  },

  // Keep typed values within 0..max so an out-of-range quantity is never submitted
  clampInputValue: function () {
    const input = this.ui.input[0];
    const value = parseInt(input.value, 10);
    let clamped = isNaN(value) || value < 0 ? 0 : value;

    if (this.state.max != null && clamped > this.state.max) {
      clamped = this.state.max;
    }

    if (String(clamped) !== input.value) {
      input.value = clamped;
    }
  },

  getInputValue: function () {
    return parseInt(this.ui.input[0].value, 10);
  },
};

export default NumberStepper;
